import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { adminApi } from '../../api';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import './CustomersList.css';
import { Button } from '../../components/Button';
import { formatCurrency } from '../../utils/currency';

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface Customer {
  user_id: string;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  status: string;
  created_at: string;
  city?: string;
  state?: string;
  occupation?: string;
  monthly_income?: number;
  total_applications: number;
  total_loans: number;
  total_active_loan_amount: number;
}

export const CustomersList: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const limit = 20;

  // Suspension modal state
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<{ userId: string; firstName: string; lastName: string } | null>(null);

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, search, statusFilter]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getCustomers(currentPage, limit, search, statusFilter);

      if (response.success && response.data) {
        setCustomers(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalItems(response.pagination.total);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchCustomers();
  };

  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'status-badge status-active';
      case 'suspended':
        return 'status-badge status-suspended';
      case 'inactive':
        return 'status-badge status-inactive';
      default:
        return 'status-badge';
    }
  };

  const handleSuspendCustomer = (userId: string, firstName: string, lastName: string) => {
    setSelectedCustomer({ userId, firstName, lastName });
    setShowSuspendModal(true);
  };

  const confirmSuspendCustomer = async () => {
    if (!selectedCustomer || !suspendReason.trim()) {
      alert('Please provide a reason for suspension');
      return;
    }

    try {
      setActionLoading(selectedCustomer.userId);
      const response = await adminApi.suspendCustomer(selectedCustomer.userId, suspendReason);

      if (response.success) {
        alert('Customer suspended successfully');
        fetchCustomers(); // Refresh the list
        setShowSuspendModal(false);
        setSuspendReason('');
        setSelectedCustomer(null);
      } else {
        alert(response.message || 'Failed to suspend customer');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to suspend customer');
    } finally {
      setActionLoading(null);
    }
  };

  const cancelSuspend = () => {
    setShowSuspendModal(false);
    setSuspendReason('');
    setSelectedCustomer(null);
  };

  const handleUnsuspendCustomer = async (userId: string, firstName: string, lastName: string) => {
    if (!window.confirm(
      `Are you sure you want to unsuspend ${firstName} ${lastName}?`
    )) {
      return;
    }

    try {
      setActionLoading(userId);
      const response = await adminApi.unsuspendCustomer(userId);

      if (response.success) {
        alert('Customer unsuspended successfully');
        fetchCustomers(); // Refresh the list
      } else {
        alert(response.message || 'Failed to unsuspend customer');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to unsuspend customer');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading && customers.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="customers-list-container">
      <div className="page-header">
        <div>
          <h1>Customer Management</h1>
          <p className="page-subtitle">Manage all customers and their profiles</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="customers-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <Button type="submit" variant="primary">
            Search
          </Button>
          {search && (
            <Button
              type="button"
              onClick={() => {
                setSearch('');
                setCurrentPage(1);
              }}
              variant="secondary"
            >
              Clear
            </Button>
          )}
        </form>

        <div className="filter-tabs">
          <button
            className={`filter-tab ${statusFilter === '' ? 'active' : ''}`}
            onClick={() => handleStatusChange('')}
          >
            All Customers ({totalItems})
          </button>
          <button
            className={`filter-tab ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => handleStatusChange('active')}
          >
            Active
          </button>
          <button
            className={`filter-tab ${statusFilter === 'suspended' ? 'active' : ''}`}
            onClick={() => handleStatusChange('suspended')}
          >
            Suspended
          </button>
          <button
            className={`filter-tab ${statusFilter === 'inactive' ? 'active' : ''}`}
            onClick={() => handleStatusChange('inactive')}
          >
            Inactive
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* Customers Table */}
      <div className="customers-table-container">
        <table className="customers-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Contact</th>
              <th>Location</th>
              <th>Occupation</th>
              <th>Income</th>
              <th>Applications</th>
              <th>Active Loans</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="text-center">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center">
                  No customers found
                </td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr key={customer.user_id}>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">
                        {customer.first_name} {customer.middle_name} {customer.last_name}
                      </div>
                      <div className="customer-id">ID: {customer.user_id.slice(0, 8)}...</div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-info">
                      <div>{customer.email}</div>
                      <div className="text-muted">{customer.phone}</div>
                    </div>
                  </td>
                  <td>
                    {customer.city && customer.state ? (
                      <div>{customer.city}, {customer.state}</div>
                    ) : (
                      <span className="text-muted">Not provided</span>
                    )}
                  </td>
                  <td>{customer.occupation || <span className="text-muted">Not provided</span>}</td>
                  <td>
                    {customer.monthly_income ? (
                      formatCurrency(customer.monthly_income)
                    ) : (
                      <span className="text-muted">Not provided</span>
                    )}
                  </td>
                  <td className="text-center">{customer.total_applications}</td>
                  <td>
                    <div className="loan-summary">
                      <div>{customer.total_loans} loan(s)</div>
                      {customer.total_active_loan_amount > 0 && (
                        <div className="text-muted">
                          {formatCurrency(customer.total_active_loan_amount)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(customer.status)}>
                      {customer.status}
                    </span>
                  </td>
                  <td>{formatDate(customer.created_at)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Link
                        to={`/admin/customers/${customer.user_id}`}
                        className="btn btn-sm btn-primary"
                      >
                        View
                      </Link>
                      {customer.status === 'active' ? (
                        <Button
                          onClick={() => handleSuspendCustomer(customer.user_id, customer.first_name, customer.last_name)}
                          disabled={actionLoading === customer.user_id}
                          variant="danger"
                          size="sm"
                          loading={actionLoading === customer.user_id}
                          style={{ minWidth: '80px' }}
                        >
                          Suspend
                        </Button>
                      ) : customer.status === 'suspended' ? (
                        <Button
                          onClick={() => handleUnsuspendCustomer(customer.user_id, customer.first_name, customer.last_name)}
                          disabled={actionLoading === customer.user_id}
                          variant="secondary"
                          size="sm"
                          loading={actionLoading === customer.user_id}
                          style={{ minWidth: '80px' }}
                        >
                          Unsuspend
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <Button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            variant="secondary"
            icon={<ChevronLeft size={20} />}
          >
            Previous
          </Button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            variant="secondary"
            iconPosition="right"
            icon={<ChevronRight size={20} />}
          >
            Next
          </Button>
        </div>
      )}

      {/* Suspend Customer Modal */}
      {showSuspendModal && selectedCustomer && (
        <div className="modal-overlay" onClick={cancelSuspend}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Suspend Customer</h2>
              <button className="modal-close" onClick={cancelSuspend}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="modal-customer-name">
                {selectedCustomer.firstName} {selectedCustomer.lastName}
              </p>
              <p className="modal-warning">
                ⚠️ This customer will be unable to login and will be notified to contact support.
              </p>
              <div className="form-group">
                <label htmlFor="suspendReason">Reason for Suspension *</label>
                <textarea
                  id="suspendReason"
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Enter the reason for suspending this customer..."
                  rows={4}
                  className="form-textarea"
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <Button
                onClick={cancelSuspend}
                variant="secondary"
                disabled={actionLoading === selectedCustomer.userId}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmSuspendCustomer}
                variant="danger"
                disabled={actionLoading === selectedCustomer.userId || !suspendReason.trim()}
                loading={actionLoading === selectedCustomer.userId}
              >
                Confirm Suspension
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
